import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdCategory,
  MdImage,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdSubdirectoryArrowRight,
  MdArrowForward,
} from "react-icons/md";
import toast from "react-hot-toast";
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../apis/categoryApi";
import { brand } from "../theme";
import { useAuth } from "../context/AuthProvider";

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Category = () => {
  const { role } = useAuth();
  const isVendor = role === "vendor";

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", image: null });
  const [editCategory, setEditCategory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const navigate = useNavigate();

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await getAllCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Failed to fetch categories");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const stats = useMemo(() => {
    let withImage = 0;
    categories.forEach((cat) => {
      if (cat.image && cat.image.trim() !== "") withImage += 1;
    });

    return {
      total: categories.length,
      withImage,
      coverage: categories.length ? Math.round((withImage / categories.length) * 100) : 0,
    };
  }, [categories]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categories;
    const q = searchTerm.toLowerCase();
    return categories.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [categories, searchTerm]);

  const paginatedCategories = useMemo(() => {
    return filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredCategories, page, rowsPerPage]);

  /* ------------------ HANDLERS ------------------ */

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setNewCategory((prev) => ({ ...prev, image: e.target.files[0] }));
    }
  };

  const handleCreate = async () => {
    if (isVendor) {
      toast.error("Vendors are not permitted to create categories");
      return;
    }
    if (!newCategory.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    setSaving(true);
    const formData = new FormData();
    formData.append("name", newCategory.name);
    if (newCategory.image) formData.append("image", newCategory.image);

    try {
      await createCategory(formData);
      toast.success("Category created successfully");
      setOpenAdd(false);
      setNewCategory({ name: "", image: null });
      fetchCategories();
    } catch (err) {
      toast.error("Failed to create category");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (isVendor) {
      toast.error("Vendors are not permitted to edit categories");
      return;
    }
    if (!editCategory?.name?.trim()) {
      toast.error("Category name is required");
      return;
    }

    setSaving(true);
    const formData = new FormData();
    formData.append("name", editCategory.name);
    if (editCategory.image instanceof File) {
      formData.append("image", editCategory.image);
    }

    try {
      await updateCategory(editCategory.id, formData);
      toast.success("Category updated successfully");
      setOpenEdit(false);
      fetchCategories();
    } catch (err) {
      toast.error("Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Category Management
            </Typography>
            <Chip
              label={`${categories.length} Categories`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Organize main service and product catalog groupings and subcategory taxonomies
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload categories">
            <IconButton
              onClick={fetchCategories}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          {!isVendor && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenAdd(true)}
              startIcon={<MdAdd size={20} />}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: "14px",
                fontWeight: 700,
                fontSize: "0.88rem",
                boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
              }}
            >
              Add Category
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard icon={<MdCategory size={24} />} label="Total Categories" count={stats.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdImage size={24} />}
            label="Visual Cover Coverage"
            count={`${stats.coverage}%`}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<MdSubdirectoryArrowRight size={24} />}
            label="Taxonomy Tree"
            count="Active"
            accent="#10B981"
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search category by name..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", sm: 340 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* View Switcher */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              bgcolor: `${brand.slateLight}20`,
              p: 0.4,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px",
                px: 1.5,
                py: 0.5,
                color: brand.slate,
                "&.Mui-selected": {
                  bgcolor: brand.paper,
                  color: brand.ink950,
                  boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                },
              },
            }}
          >
            <ToggleButton value="table">
              <MdViewList size={18} />
            </ToggleButton>
            <ToggleButton value="grid">
              <MdGridView size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Cover Image</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Category Name</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Subcategories</TableCell>
                {!isVendor && (
                  <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: isVendor ? 4 : 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedCategories.length > 0 ? (
                paginatedCategories.map((cat, idx) => (
                  <TableRow
                    key={cat.id || idx}
                    sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                  >
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {String(page * rowsPerPage + idx + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell>
                      {cat.image ? (
                        <Box
                          component="img"
                          src={cat.image}
                          alt={cat.name}
                          sx={{
                            width: 54,
                            height: 40,
                            borderRadius: "10px",
                            objectFit: "cover",
                            border: `1px solid ${brand.slateLight}50`,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 54,
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: `${brand.slateLight}30`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: brand.slate,
                          }}
                        >
                          <MdImage size={20} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/categorys/${cat.id}`}
                        style={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: brand.ink950,
                          textDecoration: "none",
                        }}
                      >
                        {cat.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        component={Link}
                        to={`/categorys/${cat.id}`}
                        endIcon={<MdArrowForward />}
                        sx={{
                          borderRadius: "10px",
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: brand.ink950,
                          bgcolor: brand.brassSoft,
                          "&:hover": { bgcolor: brand.brass, color: brand.ink950 },
                        }}
                      >
                        {isVendor ? "View Subcategories" : "Manage Subcategories"}
                      </Button>
                    </TableCell>
                    {!isVendor && (
                      <TableCell align="right">
                        <Box display="flex" gap={0.8} justifyContent="flex-end">
                          <MuiTooltip title="Edit Category">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditCategory(cat);
                                setOpenEdit(true);
                              }}
                              sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                            >
                              <MdEdit size={16} />
                            </IconButton>
                          </MuiTooltip>
                          <MuiTooltip title="Delete Category">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete({ open: true, id: cat.id })}
                              sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                            >
                              <MdDelete size={16} />
                            </IconButton>
                          </MuiTooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isVendor ? 4 : 5} align="center" sx={{ py: 8 }}>
                    <MdCategory size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No categories found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {searchTerm
                        ? "Try a different search keyword."
                        : "Click 'Add Category' to create your first grouping."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredCategories.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedCategories.map((cat) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={cat.id}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "22px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                    borderColor: `${brand.brass}66`,
                  },
                }}
              >
                <Box position="relative" height={130} bgcolor={`${brand.ink950}10`}>
                  {cat.image ? (
                    <Box
                      component="img"
                      src={cat.image}
                      alt={cat.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%" color={brand.slate}>
                      <MdImage size={36} />
                    </Box>
                  )}
                </Box>

                <Box p={2.5}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.08rem", color: brand.ink950 }} noWrap>
                    {cat.name}
                  </Typography>

                  <Button
                    fullWidth
                    size="small"
                    component={Link}
                    to={`/categorys/${cat.id}`}
                    endIcon={<MdArrowForward />}
                    sx={{
                      mt: 2,
                      borderRadius: "10px",
                      fontWeight: 700,
                      color: brand.ink950,
                      bgcolor: brand.brassSoft,
                      "&:hover": { bgcolor: brand.brass },
                    }}
                  >
                    {isVendor ? "View Subcategories" : "Subcategories"}
                  </Button>
                </Box>

                {!isVendor && (
                  <Box display="flex" gap={1} justifyContent="flex-end" p={2} pt={0}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setEditCategory(cat);
                        setOpenEdit(true);
                      }}
                      startIcon={<MdEdit />}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setConfirmDelete({ open: true, id: cat.id })}
                      startIcon={<MdDelete />}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialog: Add Category ───────────────────────────────────────── */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Add New Category
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Category Name *"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              fullWidth
            />
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: brand.slate, mb: 1 }}>
                Cover Image File
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {newCategory.image && (
                  <Box
                    component="img"
                    src={URL.createObjectURL(newCategory.image)}
                    alt="Preview"
                    sx={{ width: 80, height: 60, borderRadius: "10px", objectFit: "cover" }}
                  />
                )}
                <Button variant="outlined" component="label" sx={{ borderRadius: "10px", textTransform: "none" }}>
                  Choose Image
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAdd(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            color="secondary"
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: "10px", px: 3 }}
          >
            {saving ? "Creating..." : "Add Category"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Edit Category ──────────────────────────────────────── */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Edit Category
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Category Name *"
              value={editCategory?.name || ""}
              onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
              fullWidth
            />
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: brand.slate, mb: 1 }}>
                Cover Image File
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {editCategory?.image && (
                  <Box
                    component="img"
                    src={
                      editCategory.image instanceof File
                        ? URL.createObjectURL(editCategory.image)
                        : editCategory.image
                    }
                    alt="Preview"
                    sx={{ width: 80, height: 60, borderRadius: "10px", objectFit: "cover" }}
                  />
                )}
                <Button variant="outlined" component="label" sx={{ borderRadius: "10px", textTransform: "none" }}>
                  Replace Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setEditCategory({ ...editCategory, image: e.target.files[0] })}
                  />
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenEdit(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            color="secondary"
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: "10px", px: 3 }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Delete ─────────────────────────────────────── */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
          Delete Category?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            Are you sure you want to delete this category? All associated subcategory groupings may be affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                await deleteCategory(confirmDelete.id);
                toast.success("Category deleted");
                fetchCategories();
              } catch (err) {
                toast.error("Delete failed");
              }
              setConfirmDelete({ open: false, id: null });
            }}
            sx={{ fontWeight: 700 }}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Category;
